import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cargaMasivaMant } from '../api/client'
import { usePuedeEditarMantenimiento } from '../utils/mantenimiento'

// Operaciones > Carga masiva > Unidades / Partes: units ("Estado Vehículos") and
// partes ("Partes OyM") from monday's Excel exports, as the work orders'
// migration does: simulate first -- what would be new, and for the records
// already there every field the file would change (current -> file) --
// then apply. Only adds and updates; never deletes.

const AVISOS = ['sin_unidad', 'con_otro_abierto', 'repetidas', 'otro_proyecto', 'sin_fecha', 'unidad_por_nombre', 'columnas_nuevas', 'estados_cambiados']

export default function CargaMasiva({ tipo }) {
  const { t } = useTranslation(['engineering', 'common'])
  const puede = usePuedeEditarMantenimiento()
  const [archivo, setArchivo] = useState(null)
  const [informe, setInforme] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')
  const [campo, setCampo] = useState('')
  const [abiertos, setAbiertos] = useState(() => new Set())

  const correr = async (aplicar) => {
    if (!archivo) return
    const c = informe?.comparacion
    if (aplicar && !window.confirm(t('carga.confirmar', { nuevos: c?.nuevos.length || 0, modificados: c?.modificados.length || 0 }))) return
    setOcupado(true); setError('')
    try { setInforme(await cargaMasivaMant(tipo, archivo, aplicar)); setCampo('') }
    catch (err) {
      const d = err.response?.data?.detail
      setError(d?.message || (typeof d === 'string' ? d : t('carga.fallo')))
    }
    setOcupado(false)
  }

  const c = informe?.comparacion
  const campos = useMemo(() => {
    const n = {}
    for (const m of c?.modificados || []) for (const x of m.cambios) n[x.campo] = (n[x.campo] || 0) + 1
    return Object.entries(n).sort((a, b) => b[1] - a[1])
  }, [c])
  const modificados = (c?.modificados || []).filter(m => !campo || m.cambios.some(x => x.campo === campo))
  const borrados = (c?.modificados || []).reduce((s, m) => s + m.cambios.filter(x => x.antes != null && x.despues == null).length, 0)
  const avisos = informe ? AVISOS.filter(k => Array.isArray(informe.resumen[k]) && informe.resumen[k].length) : []
  const alternar = (k) => setAbiertos(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n })

  return (
    <div>
      {!puede && <p className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">{t('carga.soloLectura')}</p>}

      <section className="rounded-xl border border-gray-200 bg-white p-4 mb-5 space-y-3">
        <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1">
          <li>{t(`carga.paso1_${tipo}`)}</li>
          <li>{t('carga.paso2')}</li>
          <li>{t('carga.paso3')}</li>
        </ol>
        <div className="flex flex-wrap items-center gap-3">
          <input key={tipo} type="file" accept=".xlsx" disabled={!puede}
            onChange={e => { setArchivo(e.target.files?.[0] || null); setInforme(null) }} className="text-sm" />
          <button type="button" disabled={!archivo || ocupado || !puede} onClick={() => correr(false)}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
            {ocupado && !informe ? t('carga.leyendo') : t('carga.simular')}
          </button>
          {informe && !informe.aplicado && (c.nuevos.length > 0 || c.modificados.length > 0) && (
            <button type="button" disabled={ocupado} onClick={() => correr(true)}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
              {t('carga.aplicar')}
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {informe && (
          <p className={`rounded-lg px-3 py-2 text-sm ${informe.aplicado ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
            {informe.aplicado ? t('carga.aplicado') : t('carga.simulado')}
          </p>
        )}
      </section>

      {c && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <Tile n={c.nuevos.length} etiqueta={t('carga.nuevos')} />
            <Tile n={c.modificados.length} etiqueta={t('carga.conDiferencias')} tono="ojo" />
            <Tile n={c.sin_cambios} etiqueta={t('carga.sinCambios')} />
            <Tile n={borrados} etiqueta={t('carga.vaciados')} nota={t('carga.vaciadosNota')} tono={borrados ? 'mal' : null} />
          </div>

          {informe.resumen.comentarios && (
            <section className="mb-4 rounded-xl border border-sky-200 bg-sky-50/60 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-sky-900 mb-1">{t('carga.comentarios.titulo')}</h3>
              <p className="text-sm text-sky-900">
                {t('carga.comentarios.resumen', { leidos: informe.resumen.comentarios.leidos, nuevos: informe.resumen.comentarios.nuevos, existentes: informe.resumen.comentarios.existentes })}
                {informe.resumen.comentarios.sin_parte > 0 && <> {t('carga.comentarios.sinParte', { count: informe.resumen.comentarios.sin_parte })}</>}
                {informe.resumen.comentarios.sin_fecha > 0 && <> {t('carga.comentarios.sinFecha', { count: informe.resumen.comentarios.sin_fecha })}</>}
              </p>
              {informe.resumen.comentarios.con_archivos > 0 && (
                <p className="text-xs text-sky-800 mt-1">{t('carga.comentarios.archivos', { count: informe.resumen.comentarios.con_archivos })}</p>
              )}
            </section>
          )}

          {avisos.length > 0 && (
            <section className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-1">{t('carga.noSeCargan')}</h3>
              <ul className="text-sm text-amber-900 space-y-0.5">
                {avisos.map(k => (
                  <li key={k}><b>{t(`carga.avisos.${k}`, { defaultValue: k })}</b> ({informe.resumen[k].length}): <span className="text-xs">{informe.resumen[k].slice(0, 8).join(' · ')}{informe.resumen[k].length > 8 ? ' …' : ''}</span></li>
                ))}
              </ul>
            </section>
          )}

          {c.modificados.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white mb-4">
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mr-2">{t('carga.diferencias')}</h3>
                <select value={campo} onChange={e => setCampo(e.target.value)} className="border border-gray-200 rounded-md px-2 py-1 text-xs bg-white">
                  <option value="">{t('carga.todosCampos')}</option>
                  {campos.map(([k, n]) => <option key={k} value={k}>{k} ({n})</option>)}
                </select>
                <span className="text-xs text-gray-500">{t('carga.mostrando', { n: modificados.length, total: c.modificados.length })}</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {modificados.map(m => {
                  const cambios = campo ? m.cambios.filter(x => x.campo === campo) : m.cambios
                  const abierto = abiertos.has(m.clave) || cambios.length <= 3
                  return (
                    <li key={m.clave} className="px-4 py-2.5">
                      <button type="button" onClick={() => alternar(m.clave)} className="flex w-full items-center justify-between text-left">
                        <span className="text-sm font-medium text-gray-900 truncate">{m.etiqueta}</span>
                        <span className="shrink-0 text-xs text-gray-500">{t('carga.nCambios', { count: m.cambios.length })}</span>
                      </button>
                      {abierto && (
                        <table className="mt-1.5 w-full text-xs">
                          <tbody>
                            {cambios.map(x => (
                              <tr key={x.campo}>
                                <td className="py-0.5 pr-3 w-48 text-gray-500 align-top">{x.campo}</td>
                                <td className="py-0.5 pr-2 align-top"><span className="rounded bg-rose-50 px-1 text-rose-800 line-through decoration-rose-300">{x.antes ?? '—'}</span></td>
                                <td className="py-0.5 pr-2 w-4 text-gray-400 align-top">→</td>
                                <td className="py-0.5 align-top">
                                  {x.despues == null
                                    ? <span className="rounded bg-amber-100 px-1 font-medium text-amber-900">{t('carga.quedaVacio')}</span>
                                    : <span className="rounded bg-emerald-50 px-1 text-emerald-900">{x.despues}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          {c.nuevos.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('carga.listaNuevos', { count: c.nuevos.length })}</h3>
              <p className="text-xs text-gray-600 leading-relaxed">{c.nuevos.slice(0, 200).map(n => n.etiqueta).join(' · ')}</p>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function Tile({ n, etiqueta, nota, tono }) {
  const { t } = useTranslation('engineering')
  const T = { ojo: ['!', 'text-amber-700'], mal: ['▲', 'text-rose-700'] }[tono]
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{etiqueta}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{n}</p>
      {T && n > 0 && <p className={`text-[11px] font-medium ${T[1]}`}>{T[0]} {t(`carga.tono.${tono}`)}</p>}
      {nota && <p className="text-[11px] text-gray-500">{nota}</p>}
    </div>
  )
}
